import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api-client';
import { attachmentSizeError, partitionAttachmentFiles } from '@/lib/attachments';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Pagination,
  PriorityPill,
  Skeleton,
  SkeletonRow,
  SkeletonText,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  Textarea,
} from '@/components/ui';
import {
  DynamicTicketFieldRenderer,
  validateDynamicFields,
  type DynamicTicketField,
} from '@/components/forms/DynamicTicketFieldRenderer';
import type { TicketFormRule } from '@/lib/ticket-form-api';

type PortalTicket = { id: string; ticketNumber: number; subject: string; status: string; priority: string; createdAt: string };
type Conversation = { id: string; body: string; createdAt: string; user?: { firstName?: string; lastName?: string } };
function useErrorMessage() {
  const { t } = useI18n();
  return (error: unknown) => (error instanceof ApiError ? error.message : t('por.error.generic'));
}
function usePortalDates() {
  const { language } = useI18n();
  const { user } = useAuth();
  const tz = user?.timezone || undefined;
  const fmt = (value: string, options: Intl.DateTimeFormatOptions) => {
    try {
      return new Intl.DateTimeFormat(language, { ...options, ...(tz ? { timeZone: tz } : {}) }).format(new Date(value));
    } catch {
      return new Date(value).toLocaleString(language);
    }
  };
  return {
    formatDate: (value: string) => fmt(value, { dateStyle: 'medium' }),
    formatDateTime: (value: string) => fmt(value, { dateStyle: 'medium', timeStyle: 'short' }),
  };
}
function Failure({ text, retry }: { text: string; retry: () => void }) { return <ErrorState message={text} onRetry={retry} />; }


export default function PortalPage() {
  const { t } = useI18n();
  const errorMessage = useErrorMessage();
  const { formatDate } = usePortalDates();
  const STATUS_TABS = [
    { value: 'all', label: t('por.tab.all') },
    { value: 'open', label: t('status.open') },
    { value: 'pending', label: t('status.pending') },
    { value: 'resolved', label: t('status.resolved') },
    { value: 'closed', label: t('status.closed') },
  ];
  const [tickets, setTickets] = React.useState<PortalTicket[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [status, setStatus] = React.useState<string>('all');
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [meta, setMeta] = React.useState<{ page: number; limit: number; total: number; totalPages: number }>({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<{ data: PortalTicket[]; meta: typeof meta }>('/portal/tickets', {
        params: { status, search: search || undefined, page, limit: 10 },
      });
      setTickets(res.data);
      setMeta(res.meta);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  React.useEffect(() => { void load(); }, [load]);

  // Debounce search input
  const [searchInput, setSearchInput] = React.useState('');
  React.useEffect(() => {
    const handle = window.setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('por.list.title')}
        description={t('por.list.description')}
        className="mb-0"
        actions={
          <>
            <Link to="/portal/submit"><Button variant="accent">{t('por.list.newRequest')}</Button></Link>
            <Link to="/portal/kb"><Button variant="outline">{t('por.list.knowledgeBase')}</Button></Link>
          </>
        }
      />

      {/* Status tabs */}
      <Tabs
        aria-label={t('por.list.filterByStatus')}
        items={STATUS_TABS}
        value={status}
        onChange={(value) => { setStatus(value); setPage(1); }}
      />

      {/* Search */}
      <Input
        placeholder={t('por.list.searchPlaceholder')}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />

      {error ? (
        <Failure text={error} retry={load} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {loading ? <Skeleton className="h-4 w-28" /> : t(meta.total !== 1 ? 'por.list.count' : 'por.list.countOne', { count: meta.total })}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <SkeletonRow rows={5} />
            ) : tickets.length === 0 ? (
              <EmptyState
                title={search || status !== 'all' ? t('por.list.emptyFilteredTitle') : t('por.list.emptyTitle')}
                description={
                  search || status !== 'all'
                    ? t('por.list.emptyFilteredDescription')
                    : t('por.list.emptyDescription')
                }
                action={<Link to="/portal/submit"><Button variant="accent">{t('por.list.submitFirst')}</Button></Link>}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('por.col.request')}</TableHead>
                    <TableHead>{t('por.col.subject')}</TableHead>
                    <TableHead>{t('por.col.status')}</TableHead>
                    <TableHead>{t('por.col.priority')}</TableHead>
                    <TableHead>{t('por.col.created')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.id}>
                      <TableCell><Link className="text-brand-700 hover:underline" to={`/portal/tickets/${ticket.id}`}>#{ticket.ticketNumber}</Link></TableCell>
                      <TableCell>{ticket.subject}</TableCell>
                      <TableCell><StatusPill value={ticket.status} /></TableCell>
                      <TableCell><PriorityPill value={ticket.priority} /></TableCell>
                      <TableCell>{formatDate(ticket.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <Pagination page={meta.page} pageSize={meta.limit} total={meta.total} onPageChange={setPage} />
      )}
    </div>
  );
}

export function PortalTicketDetailPage() { const { id } = useParams<{ id: string }>(); const { t } = useI18n(); const errorMessage = useErrorMessage(); const { formatDateTime } = usePortalDates(); const [ticket, setTicket] = React.useState<(PortalTicket & { description: string; conversations: Conversation[]; attachments?: { id: string; fileName: string; fileSize: number; contentType: string; createdAt: string }[] }) | null>(null); const [body, setBody] = React.useState(''); const [replyFiles, setReplyFiles] = React.useState<File[]>([]); const [loading, setLoading] = React.useState(true); const [sending, setSending] = React.useState(false); const [error, setError] = React.useState(''); const load = React.useCallback(async () => { if (!id) return; setLoading(true); setError(''); try { setTicket(await api.get<typeof ticket>(`/portal/tickets/${id}`)); } catch (e) { setError(errorMessage(e)); } finally { setLoading(false); } }, [id]); React.useEffect(() => { void load(); }, [load]); async function reply(e: React.FormEvent) { e.preventDefault(); if (!id || body.trim().length < 1) return; setSending(true); setError(''); try { await api.post(`/portal/tickets/${id}/reply`, { body: body.trim() }); for (const file of replyFiles) { const fd = new FormData(); fd.append('file', file); fd.append('ticketId', id); await api.upload('/portal/attachments', fd); } setBody(''); setReplyFiles([]); await load(); } catch (e) { setError(errorMessage(e)); } finally { setSending(false); } } if (loading) return <div className="mx-auto max-w-3xl space-y-6"><Skeleton className="h-7 w-2/3" /><Card><CardContent className="pt-6"><SkeletonText lines={4} /></CardContent></Card></div>; if (error || !ticket) return <Failure text={error || t('por.error.requestNotFound')} retry={load} />; return <div className="mx-auto max-w-3xl space-y-6"><PageHeader breadcrumb={[{ label: t('por.detail.myRequests'), to: '/portal' }, { label: `#${ticket.ticketNumber}` }]} title={ticket.subject} className="mb-0" actions={['resolved', 'closed'].includes(ticket.status) ? <Button variant="outline" onClick={async () => { try { await api.put(`/portal/tickets/${id}/reopen`); await load(); } catch (e) { setError(errorMessage(e)); } }}>{t('por.detail.reopen')}</Button> : undefined} /><div className="flex flex-wrap items-center gap-2"><StatusPill value={ticket.status} /><PriorityPill value={ticket.priority} /></div><Card><CardContent className="pt-6"><p className="whitespace-pre-wrap text-sm text-ink-muted">{ticket.description}</p></CardContent></Card>{ticket.attachments && ticket.attachments.length > 0 && <Card><CardHeader><CardTitle>{t('por.detail.attachments')}</CardTitle></CardHeader><CardContent><ul className="space-y-1">{ticket.attachments.map((att) => <li key={att.id} className="flex items-center justify-between rounded border border-line px-3 py-2 text-sm"><span className="truncate">{att.fileName} <span className="text-ink-subtle">({t('por.file.sizeKb', { size: (att.fileSize / 1024).toFixed(0) })})</span></span><a href={`/api/attachments/${att.id}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-brand-700 hover:underline">{t('por.detail.download')}</a></li>)}</ul></CardContent></Card>}<Card><CardHeader><CardTitle>{t('por.detail.conversation')}</CardTitle></CardHeader><CardContent className="space-y-4">{ticket.conversations.length === 0 ? <EmptyState title={t('por.detail.noReplies')} description={t('por.detail.noRepliesHint')} className="py-8" /> : ticket.conversations.map((conversation) => <div className="rounded-lg border p-4" key={conversation.id}><div className="mb-2 text-xs text-ink-subtle">{conversation.user?.firstName} {conversation.user?.lastName} · {formatDateTime(conversation.createdAt)}</div><p className="whitespace-pre-wrap text-sm">{conversation.body}</p></div>)}<form className="space-y-3 border-t pt-4" onSubmit={reply}><Textarea id="portal-reply" label={t('por.detail.reply')} className="min-h-28" value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('por.detail.replyPlaceholder')} required /><div className="space-y-1"><input type="file" multiple onChange={(e) => { const selected = e.target.files ? Array.from(e.target.files) : []; e.target.value = ''; const { accepted, rejected } = partitionAttachmentFiles(selected); if (rejected.length > 0) setError(attachmentSizeError(rejected)); if (accepted.length > 0) setReplyFiles((prev) => [...prev, ...accepted]); }} className="block text-sm text-ink-subtle file:mr-3 file:rounded file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100" />{replyFiles.length > 0 && <ul className="space-y-1">{replyFiles.map((file, idx) => <li key={`${file.name}-${idx}`} className="flex items-center justify-between rounded border border-line px-3 py-1.5 text-sm"><span className="truncate">{file.name} <span className="text-ink-subtle">({t('por.file.sizeKb', { size: (file.size / 1024).toFixed(0) })})</span></span><button type="button" className="ml-2 text-danger-600 hover:text-danger-600" onClick={() => setReplyFiles((prev) => prev.filter((_, i) => i !== idx))} aria-label={t('por.file.remove', { name: file.name })}>&times;</button></li>)}</ul>}</div><Button type="submit" disabled={sending || !body.trim()}>{sending ? t('por.detail.sending') : t('por.detail.sendReply')}</Button></form></CardContent></Card></div>; }

type PortalTicketFormField = {
  fieldKey?: string;
  name?: string;
  label: string;
  labelForCustomers?: string | null;
  description?: string | null;
  fieldType?: string;
  rendererType?: string | null;
  sourceType?: string | null;
  requiredForAgents?: boolean;
  requiredForCustomers?: boolean;
  displayedToCustomers?: boolean;
  customersCanEdit?: boolean;
  choices?: unknown;
  options?: unknown;
  dependsOnFieldKey?: string | null;
  enabled?: boolean;
  requiredOverride?: boolean | null;
  visibleToCustomers?: boolean;
};

type PortalTicketFormResponse = {
  formId: string;
  formName: string;
  rules: TicketFormRule[];
  fields: PortalTicketFormField[];
};

const PORTAL_SYSTEM_FIELD_KEYS = new Set(['requester', 'subject', 'description', 'status', 'priority', 'source', 'ticket_type', 'agent', 'group', 'company']);

export function PortalSubmitPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const errorMessage = useErrorMessage();
  const [formData, setFormData] = React.useState<PortalTicketFormResponse | null>(null);
  const [values, setValues] = React.useState<Record<string, unknown>>({});
  const [files, setFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});

  const dynamicFields = React.useMemo<DynamicTicketField[]>(() => (formData?.fields ?? []).map((field) => ({
    fieldKey: field.fieldKey ?? field.name ?? '',
    label: field.label,
    description: field.description ?? null,
    labelForCustomers: field.labelForCustomers ?? null,
    sourceType: field.sourceType ?? undefined,
    rendererType: field.rendererType ?? field.fieldType ?? 'single_line_text',
    requiredForAgents: field.requiredForAgents ?? false,
    requiredForCustomers: field.requiredForCustomers ?? false,
    displayedToCustomers: field.displayedToCustomers !== false,
    customersCanEdit: field.customersCanEdit !== false,
    rawChoices: field.options ?? field.choices ?? null,
    choices: field.choices ?? field.options ?? null,
    dependsOnFieldKey: field.dependsOnFieldKey ?? null,
    enabled: field.enabled !== false,
    requiredOverride: field.requiredOverride ?? null,
    visibleToCustomers: field.visibleToCustomers !== false,
  })), [formData]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ data: PortalTicketFormResponse }>('/portal/ticket-fields');
      setFormData(response.data);
      setValues({});
      setFieldErrors({});
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  function setValue(fieldKey: string, value: unknown) {
    setValues((current) => ({ ...current, [fieldKey]: value }));
    setFieldErrors((current) => {
      if (!current[fieldKey]) return current;
      const next = { ...current };
      delete next[fieldKey];
      return next;
    });
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!formData || saving) return;

    const nextFieldErrors = validateDynamicFields(dynamicFields, values, 'customer', formData.rules, (label) => t('fields.required', { label }));
    const subject = String(values.subject ?? '').trim();
    const description = String(values.description ?? '').trim();
    if (!subject) nextFieldErrors.subject = t('por.submit.subjectRequired');
    if (!description) nextFieldErrors.description = t('por.submit.descriptionRequired');
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    const customFields = Object.fromEntries(
      Object.entries(values).filter(([fieldKey, value]) => (
        !PORTAL_SYSTEM_FIELD_KEYS.has(fieldKey)
        && value !== undefined
        && value !== null
        && (!(typeof value === 'string') || value.trim() !== '')
        && (!(Array.isArray(value)) || value.length > 0)
      )),
    );

    setSaving(true);
    setError('');
    try {
      const result = await api.post<PortalTicket>('/portal/tickets', {
        formId: formData.formId,
        subject,
        description,
        priority: String(values.priority ?? 'medium').toLowerCase(),
        customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
      });

      // Upload attachments after ticket creation
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('ticketId', result.id);
        await api.upload('/portal/attachments', fd);
      }

      navigate(`/portal/tickets/${result.id}`);
    } catch (submitError) {
      if (submitError instanceof ApiError) {
        const errorData = submitError.data;
        const errors = errorData && typeof errorData === 'object' && 'errors' in errorData
          ? (errorData as { errors?: unknown }).errors
          : undefined;
        if (errors && typeof errors === 'object') {
          setFieldErrors(errors as Record<string, string>);
          return;
        }
      }
      setError(errorMessage(submitError));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Skeleton className="h-4 w-40" />
      <Card>
        <CardContent className="space-y-5 pt-6" aria-busy="true">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="space-y-2">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
  if (error || !formData) return <Failure text={error || t('por.error.formUnavailable')} retry={load} />;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Breadcrumb items={[{ label: t('por.detail.myRequests'), to: '/portal' }, { label: t('por.submit.newRequest') }]} />
      <Card>
        <CardHeader>
          <CardTitle>{formData.formName || t('por.submit.defaultTitle')}</CardTitle>
          <p className="text-sm text-ink-subtle">{t('por.submit.intro')}</p>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            {error && <p className="text-sm text-danger-600" role="alert">{error}</p>}
            <DynamicTicketFieldRenderer
              fields={dynamicFields}
              values={values}
              errors={fieldErrors}
              rules={formData.rules}
              mode="customer"
              onChange={setValue}
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-ink-muted">{t('por.submit.attachments')}</label>
              <input
                type="file"
                multiple
                className="block w-full text-sm text-ink-subtle file:mr-3 file:rounded file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
                onChange={(e) => {
                  const selected = e.target.files ? Array.from(e.target.files) : [];
                  e.target.value = '';
                  const { accepted, rejected } = partitionAttachmentFiles(selected);
                  if (rejected.length > 0) setError(attachmentSizeError(rejected));
                  if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
                }}
              />
              {files.length > 0 && (
                <ul className="space-y-1">
                  {files.map((file, idx) => (
                    <li key={`${file.name}-${idx}`} className="flex items-center justify-between rounded border border-line px-3 py-1.5 text-sm">
                      <span className="truncate">{file.name} <span className="text-ink-subtle">({t('por.file.sizeKb', { size: (file.size / 1024).toFixed(0) })})</span></span>
                      <button type="button" className="ml-2 text-danger-600 hover:text-danger-600" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))} aria-label={t('por.file.remove', { name: file.name })}>&times;</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button type="submit" variant="accent" disabled={saving}>{saving ? t('por.submit.submitting') : t('por.submit.submit')}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

type Article = { id: string; title: string; content?: string; views?: number; createdAt?: string; folder?: { name?: string; category?: { name?: string } } };
type KbFolder = { id: string; name: string; _count: { articles: number } };
type KbCategory = { id: string; name: string; description?: string | null; folders: KbFolder[] };
type FolderMeta = { id: string; name: string; category: { id: string; name: string } };

export function PortalKbPage() {
  const { t } = useI18n();
  const errorMessage = useErrorMessage();
  const [view, setView] = React.useState<'categories' | 'folder' | 'search'>('categories');
  const [categories, setCategories] = React.useState<KbCategory[]>([]);
  const [folderMeta, setFolderMeta] = React.useState<FolderMeta | null>(null);
  const [articles, setArticles] = React.useState<Article[]>([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const loadCategories = React.useCallback(async () => {
    setLoading(true); setError('');
    try { setCategories(await api.get<KbCategory[]>('/kb/categories')); } catch (e) { setError(errorMessage(e)); } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { void loadCategories(); }, [loadCategories]);

  async function openFolder(folderId: string) {
    setView('folder'); setLoading(true); setError('');
    try {
      const res = await api.get<{ folder: FolderMeta; articles: Article[] }>(`/kb/folders/${folderId}/articles`);
      setFolderMeta(res.folder); setArticles(res.articles);
    } catch (e) { setError(errorMessage(e)); } finally { setLoading(false); }
  }

  React.useEffect(() => {
    if (!search) { if (view === 'search') { setView('categories'); } return; }
    setView('search'); setLoading(true); setError('');
    const handle = window.setTimeout(async () => {
      try { setArticles(await api.get<Article[]>('/kb/articles', { params: { search } })); } catch (e) { setError(errorMessage(e)); } finally { setLoading(false); }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  function breadcrumbs() {
    const base = <button type="button" className="text-brand-700 hover:underline" onClick={() => { setView('categories'); setSearch(''); }}>{t('por.kb.crumbRoot')}</button>;
    if (view === 'search') return <nav className="flex items-center gap-1 text-sm text-ink-subtle">{base}<span>/</span><span>{t('por.kb.searchResults')}</span></nav>;
    if (view === 'folder' && folderMeta) return <nav className="flex items-center gap-1 text-sm text-ink-subtle">{base}<span>/</span><button type="button" className="text-brand-700 hover:underline" onClick={() => { setView('categories'); setSearch(''); }}>{folderMeta.category.name}</button><span>/</span><span>{folderMeta.name}</span></nav>;
    return null;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={[{ label: t('por.kb.portal'), to: '/portal' }, { label: t('por.kb.title') }]}
        title={t('por.kb.title')}
        description={t('por.kb.description')}
        className="mb-0"
      />
      {breadcrumbs()}
      <Input placeholder={t('por.kb.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
      {error ? <Failure text={error} retry={view === 'categories' ? loadCategories : () => { if (folderMeta) void openFolder(folderMeta.id); }} /> : loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((card) => (
            <Card key={card}>
              <CardContent className="space-y-3 pt-6" aria-busy="true">
                <Skeleton className="h-4 w-2/5" />
                <SkeletonText lines={2} />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : view === 'categories' ? (
        categories.length === 0 ? <Card><CardContent className="p-0"><EmptyState title={t('por.kb.emptyTitle')} description={t('por.kb.emptyDescription')} /></CardContent></Card> : (
          <div className="grid gap-4 md:grid-cols-2">
            {categories.map((cat) => (
              <Card key={cat.id}>
                <CardHeader><CardTitle>{cat.name}</CardTitle>{cat.description && <p className="text-xs text-ink-subtle">{cat.description}</p>}</CardHeader>
                <CardContent className="space-y-1">
                  {cat.folders.length === 0 ? <p className="text-sm text-ink-subtle">{t('por.kb.noFolders')}</p> : cat.folders.map((f) => (
                    <button key={f.id} type="button" className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-surface-subtle" onClick={() => void openFolder(f.id)}>
                      <span>{f.name}</span><span className="text-xs text-ink-subtle">{t(f._count.articles !== 1 ? 'por.kb.articles' : 'por.kb.articlesOne', { count: f._count.articles })}</span>
                    </button>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        articles.length === 0 ? <Card><CardContent className="p-0"><EmptyState title={search ? t('por.kb.noMatch', { query: search }) : t('por.kb.folderEmpty')} description={search ? t('por.kb.noMatchHint') : t('por.kb.folderEmptyHint')} action={<Link to="/portal/submit"><Button variant="accent">{t('por.kb.submitRequest')}</Button></Link>} /></CardContent></Card> : (
          <div className="grid gap-4 md:grid-cols-2">
            {articles.map((article) => (
              <Link to={`/portal/kb/${article.id}`} key={article.id}>
                <Card className="h-full transition hover:border-brand-400">
                  <CardHeader><CardTitle>{article.title}</CardTitle>{article.folder && <p className="text-xs text-ink-subtle">{article.folder.category?.name} · {article.folder.name}</p>}</CardHeader>
                  <CardContent>{article.content ? <p className="line-clamp-3 text-sm text-ink-muted">{article.content.replace(/<[^>]+>/g, ' ')}</p> : <p className="text-xs text-ink-subtle">{t('por.kb.views', { count: article.views ?? 0 })}</p>}</CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export function PortalKbArticlePage() { const { id } = useParams<{ id: string }>(); const { t } = useI18n(); const errorMessage = useErrorMessage(); const [article, setArticle] = React.useState<Article | null>(null); const [loading, setLoading] = React.useState(true); const [error, setError] = React.useState(''); const [feedbackGiven, setFeedbackGiven] = React.useState<'up' | 'down' | null>(null); const load = React.useCallback(async () => { if (!id) return; setLoading(true); setError(''); try { setArticle(await api.get<Article>(`/kb/articles/${id}`)); } catch (e) { setError(errorMessage(e)); } finally { setLoading(false); } }, [id]); React.useEffect(() => { void load(); }, [load]); async function feedback(value: 'up' | 'down') { if (!id || feedbackGiven) return; try { await api.post(`/kb/articles/${id}/feedback`, { value }); setFeedbackGiven(value); } catch (e) { setError(errorMessage(e)); } } if (loading) return <div className="mx-auto max-w-3xl space-y-6"><Skeleton className="h-4 w-40" /><Card><CardContent className="space-y-4 pt-6" aria-busy="true"><Skeleton className="h-6 w-3/5" /><SkeletonText lines={6} /></CardContent></Card></div>; if (error || !article) return <Failure text={error || t('por.error.articleNotFound')} retry={load} />; return <div className="mx-auto max-w-3xl space-y-6"><Breadcrumb items={[{ label: t('por.kb.portal'), to: '/portal' }, { label: t('por.kb.title'), to: '/portal/kb' }, { label: article.title }]} /><Card><CardHeader><CardTitle>{article.title}</CardTitle><p className="text-xs text-ink-subtle">{article.folder?.category?.name} · {article.folder?.name}</p></CardHeader><CardContent><div className="prose max-w-none text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content || '') }} /><div className="mt-8 flex items-center gap-3 border-t pt-4">{feedbackGiven ? <span className="text-sm text-success-700">{t('por.kb.thanks')}</span> : <><span className="text-sm text-ink-subtle">{t('por.kb.helpful')}</span><Button size="sm" variant="outline" onClick={() => void feedback('up')}>{t('por.kb.yes')}</Button><Button size="sm" variant="outline" onClick={() => void feedback('down')}>{t('por.kb.no')}</Button></>}</div></CardContent></Card></div>; }

export function PortalCsatPage() { const { id } = useParams<{ id: string }>(); const { t } = useI18n(); const errorMessage = useErrorMessage(); const [survey, setSurvey] = React.useState<{ ticketNumber: number; subject: string; error?: string } | null>(null); const [rating, setRating] = React.useState(0); const [comment, setComment] = React.useState(''); const [loading, setLoading] = React.useState(true); const [saving, setSaving] = React.useState(false); const [error, setError] = React.useState(''); const [done, setDone] = React.useState(false); const load = React.useCallback(async () => { if (!id) return; setLoading(true); setError(''); try { setSurvey(await api.get<typeof survey>(`/portal/csat/${id}`)); } catch (e) { setError(errorMessage(e)); } finally { setLoading(false); } }, [id]); React.useEffect(() => { void load(); }, [load]); async function submit(e: React.FormEvent) { e.preventDefault(); if (!id || rating < 1) { setError(t('por.error.selectRating')); return; } setSaving(true); setError(''); try { await api.post(`/portal/csat/${id}`, { rating, comment }); setDone(true); } catch (e) { setError(errorMessage(e)); } finally { setSaving(false); } } if (loading) return <div className="mx-auto max-w-lg"><Card><CardContent className="space-y-4 pt-6" aria-busy="true"><Skeleton className="h-5 w-2/5" /><Skeleton className="h-11 w-64" /><SkeletonText lines={3} /></CardContent></Card></div>; if (error && !survey) return <Failure text={error} retry={load} />; if (done) return <Card><CardContent className="p-8 text-center"><h1 className="text-xl font-bold">{t('por.csat.thanksTitle')}</h1><p className="mt-2 text-sm text-ink-subtle">{t('por.csat.thanksBody')}</p></CardContent></Card>; return <div className="mx-auto max-w-lg"><Card><CardHeader><CardTitle>{t('por.csat.title')}</CardTitle><p className="text-sm text-ink-subtle">{t('por.csat.request', { number: survey?.ticketNumber ?? '', subject: survey?.subject ?? '' })}</p></CardHeader><CardContent><form className="space-y-5" onSubmit={submit}>{error && <p className="text-sm text-danger-600">{error}</p>}<div><p className="mb-2 text-sm font-medium">{t('por.csat.rating')}</p><div className="flex gap-2">{[1, 2, 3, 4, 5].map((value) => <button type="button" key={value} aria-label={t('por.csat.stars', { count: value })} onClick={() => setRating(value)} className={`h-11 w-11 rounded border text-lg ${rating >= value ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line-strong'}`}>★</button>)}</div></div><Textarea label={t('por.csat.comment')} className="min-h-28" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} /><Button type="submit" variant="accent" disabled={saving}>{saving ? t('por.submit.submitting') : t('por.csat.submit')}</Button></form></CardContent></Card></div>; }
